// Enhanced error handling with retry logic and exponential backoff
export class APIError extends Error {
  constructor(message, status, code, originalError) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = code;
    this.originalError = originalError;
  }
}

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const isRetryableError = (error) => {
  // Retry on network errors, timeouts, and 5xx server errors
  if (!error.response) return true; // Network error
  const status = error.response.status;
  return status >= 500 || status === 408 || status === 429; // Server errors, timeout, rate limit
};

export const withRetry = async (fn, options = {}) => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    onRetry = () => {}
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on the last attempt or non-retryable errors
      if (attempt === maxRetries || !isRetryableError(error)) {
        break;
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = Math.min(
        baseDelay * Math.pow(backoffFactor, attempt) + Math.random() * 1000,
        maxDelay
      );
      
      onRetry(error, attempt + 1, delay);
      await sleep(delay);
    }
  }
  
  // Transform the error into our custom APIError
  const status = lastError.response?.status;
  const message = lastError.response?.data?.message || lastError.message;
  const code = lastError.response?.data?.code || lastError.code;
  
  throw new APIError(message, status, code, lastError);
};

export const handleAPIError = (error, context = '') => {
  console.error(`API Error${context ? ` in ${context}` : ''}:`, {
    message: error.message,
    status: error.status,
    code: error.code,
    originalError: error.originalError
  });
  
  // Return user-friendly error messages
  if (error.status === 401) {
    return 'Your session has expired. Please log in again.';
  } else if (error.status === 403) {
    return 'You do not have permission to perform this action.';
  } else if (error.status === 404) {
    return 'The requested resource was not found.';
  } else if (error.status === 429) {
    return 'Too many requests. Please try again later.';
  } else if (error.status >= 500) {
    return 'Server error. Please try again later.';
  } else if (!error.status) {
    return 'Network error. Please check your connection and try again.';
  } else {
    return error.message || 'An unexpected error occurred.';
  }
};

// Enhanced API client wrapper with automatic retry
export const createAPICall = (apiClient, endpoint, method = 'GET') => {
  return async (data = null, config = {}) => {
    const { retryOptions, ...requestConfig } = config;
    
    return withRetry(
      async () => {
        switch (method.toUpperCase()) {
          case 'GET':
            return await apiClient.get(endpoint, requestConfig);
          case 'POST':
            return await apiClient.post(endpoint, data, requestConfig);
          case 'PUT':
            return await apiClient.put(endpoint, data, requestConfig);
          case 'DELETE':
            return await apiClient.delete(endpoint, requestConfig);
          default:
            throw new Error(`Unsupported HTTP method: ${method}`);
        }
      },
      {
        maxRetries: 2,
        baseDelay: 1000,
        onRetry: (error, attempt, delay) => {
          console.warn(`API call failed, retrying in ${delay}ms (attempt ${attempt}):`, error.message);
        },
        ...retryOptions
      }
    );
  };
};